import React, { useEffect, useState } from 'react';
import { getFirestore, collection, getDocs, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { useAdminThreadMessages } from '../utils/useAdminThreadMessages';
import ThreadView from './ThreadView';
import { useAdminThread } from '../utils/useAdminThread';

const db = getFirestore();

function parseUserFromDocId(docId) {
  // docId format: itemId_email
  const parts = docId.split('_');
  return parts.slice(1).join('_'); // everything after first underscore
}

export default function AdminOffersModal({ isOpen, onClose, itemId, itemTitle }) {
  const [threads, setThreads] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const { messages, loading: threadLoading, error: threadError, sendMessage } = useAdminThreadMessages(itemId, selectedUser, 'hello@titledworks.com', itemTitle);
  const { messages: adminThreadMessages, loading: adminThreadLoading, error: adminThreadError } = useAdminThread(db, itemId, selectedUser);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    async function fetchThreads() {
      const offersCol = collection(db, 'offers');
      const snapshot = await getDocs(offersCol);
      const filtered = [];
      snapshot.forEach(docSnap => {
        if (docSnap.id.startsWith(itemId + '_')) {
          filtered.push({ id: docSnap.id, ...docSnap.data() });
        }
      });
      setThreads(filtered);
      setLoading(false);
    }
    fetchThreads();
  }, [isOpen, itemId]);

  if (!isOpen) return null;

  const handleLikeMessage = async (msgIdx) => {
    console.log('Like message', msgIdx);
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || sending) return;
    setSending(true);
    const success = await sendMessage(reply, true);
    if (success) {
      setReply('');
    }
    setSending(false);
  };
  
  // Drawer/backdrop structure
  return (
    <div className="fixed inset-0 z-50 pointer-events-none">
      {/* Backdrop */}
      <div
        className={`
          fixed inset-0 bg-black bg-opacity-30 transition-opacity duration-300
          ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
        onClick={onClose}
        style={{ zIndex: 10 }}
      />
      {/* Drawer */}
      <div
        className={`
          fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl p-6 
          transform transition-transform duration-300 flex flex-col max-h-screen overflow-y-auto
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
          pointer-events-auto
        `}
        style={{ zIndex: 20, willChange: 'transform' }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        {/* List view */}
        {!selectedUser ? (
          <>
            <h2 className="text-2xl mb-4">Offers for this item</h2>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading threads…</div>
            ) : threads.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No offers yet for this item.</div>
            ) : (
              <ul className="space-y-4 flex flex-col">
                {threads.map(thread => (
                  <li
                    key={thread.id}
                    className="py-3 px-4 cursor-pointer bg-gray-100 hover:opacity-80 rounded"
                    onClick={() => setSelectedUser(parseUserFromDocId(thread.id))}
                  >
                    <div>{parseUserFromDocId(thread.id)}</div>
                    <div className="text-xs text-gray-500 line-clamp-1">
                      {thread.messages && thread.messages.length > 0
                        ? thread.messages[thread.messages.length - 1].message
                        : 'No messages'}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          // Thread view
          <>
            <button className="mb-4 text-sm text-gray-600 hover:underline text-left" onClick={() => setSelectedUser(null)}>
              ← Back to all offers
            </button>
            <p className="text-lg mb-4">Thread with {selectedUser}</p>
            <div className="h-full space-y-2 h-full">
              {(() => {
                const thread = threads.find(t => parseUserFromDocId(t.id) === selectedUser);
                return thread && thread.messages && thread.messages.length > 0 ? (
                  <ThreadView
                    messages={adminThreadMessages}
                    loading={adminThreadLoading}
                    error={adminThreadError}
                    onLikeMessage={handleLikeMessage}
                    currentUserEmail="hello@titledworks.com"
                    messageText={reply}
                    setMessageText={setReply}
                    onSendMessage={handleSendReply}
                    sending={sending}
                  />
                ) : (
                  <div className="text-gray-500">No messages yet.</div>
                );
              })()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}